
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useEmailVerification = () => {
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [lastResendTime, setLastResendTime] = useState<number | null>(null);

  const resendVerification = useCallback(async (email: string) => {
    // Check cooldown (60 seconds)
    const now = Date.now();
    if (lastResendTime && now - lastResendTime < 60000) {
      const remainingTime = Math.ceil((60000 - (now - lastResendTime)) / 1000);
      setResendCooldown(remainingTime);
      throw new Error(`Please wait ${remainingTime} seconds before resending`);
    }

    setIsResending(true);
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        throw error;
      }

      setLastResendTime(now);
      
      // Start cooldown timer
      let countdown = 60;
      setResendCooldown(countdown);
      
      const timer = setInterval(() => {
        countdown--;
        setResendCooldown(countdown);
        
        if (countdown <= 0) {
          clearInterval(timer);
          setResendCooldown(0);
        }
      }, 1000);

      return true;
    } catch (error) {
      console.error('Error resending verification:', error);
      throw error;
    } finally {
      setIsResending(false);
    }
  }, [lastResendTime]);

  return {
    resendVerification,
    isResending,
    resendCooldown
  };
};
